import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { UntypedFormGroup, UntypedFormControl, Validators } from '@angular/forms';
import { AcuityShiftRecordService } from 'src/shared/services/shift-record-acuity.service';
import { ShiftRecordStatus } from 'src/shared/models/shift-record-status';
import { WorkflowUtilityService } from 'src/shared/services/workflow-utility.service';
import { AncillaryWorkflowPage } from 'src/shared/models/workflow-page-ancllary.model';
import { AncillaryConfiguration } from 'src/shared/models/ancillary-configuration.model';

import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import * as moment from 'moment';

import { Router } from '@angular/router';
import { PageInterface } from '../planner/page/page.interface';
import { WorkflowPageComponent } from '../base-classes/workflow-page/workflow-page.component';
import { ShiftBlock } from 'src/shared/models/shift-block.model';
import { UnitOfServiceRecordService } from 'src/shared/services/uos-record.service';
import { UnitOfServiceRecord } from 'src/shared/models/unit-of-service-record.model';
import { ShiftBlockService } from 'src/shared/services/shift-block.service';
import { Constants } from 'src/shared/utilities/constants.util';
import { WorkflowConstants } from '../workflow-constants.util';
import { ImportTotalUosModalComponent } from '../import-total-uos-modal/import-total-uos-modal.component';
import { HsgModal } from '@hhsg/hsg-asc';
import { WorkflowPageUtil } from '../base-classes/workflow-page/workflow-page.util';
import { MiscUtil } from 'src/shared/utilities/misc.util';

@Component({
  selector: 'smt-enter-uos',
  templateUrl: './enter-uos.component.html',
  styleUrls: ['./enter-uos.component.scss']
})
export class EnterUOSComponent extends WorkflowPageComponent implements OnInit, PageInterface {
    @Input() page: AncillaryWorkflowPage;
    @Output() previousRequested = new EventEmitter();

    public enterUOSForm:UntypedFormGroup = new UntypedFormGroup({});
    public enterUOSFormValues$;
    public formGroups: UntypedFormGroup[];
    public shiftBlocks: ShiftBlock[] = [];
    public uosRecords: UnitOfServiceRecord[] = [];
    public loadingRecords: boolean;
    public totalUos = 0;
    private minValue = WorkflowConstants.UosMinValue;
    private maxValue = WorkflowConstants.UosMaxValue;
    private workflowDate: string;
    private editingImportedRecords: boolean;
    loading : boolean = false;

    get unitOfServiceName(): string {
        return this.page.unitOfServiceName;
    }

    get ancillaryConfiguration(): AncillaryConfiguration {
        return this.shiftRecord.costCenter.ancillaryConfiguration;
    }

    constructor(protected _router: Router,
        private _shiftRecordService: AcuityShiftRecordService,
        private _utility: WorkflowUtilityService,
        private _uosRecordService: UnitOfServiceRecordService,
        private _shiftBlockService: ShiftBlockService,
        private _modal: HsgModal) {
            super(_router);
    }

    async ngOnInit() {
        this.loadingRecords = true;
        super.initialize(this.page, () => this.initialize());
    }

    initialize() {
        if (this.ancillaryConfiguration.enterUOSByBlock) {
            if (this.shiftRecord.prevShiftRecord && this.shiftRecord.prevShiftRecord.shiftBlock) {
                this.workflowDate = this.shiftRecord.prevShiftRecord.workflowTimeFacility;
                this.shiftBlocks.push(this.shiftRecord.prevShiftRecord.shiftBlock);
                this.initializeForm();
            } else {
                this.loadingRecords = false;
            }
        } else {
            this._shiftBlockService.findPreviousUosWorkingDay(this.shiftRecord.costCenterId, this.shiftRecord.workflowTimeFacility).then((previousDate) => {
                if (previousDate) {
                    this.workflowDate = moment(previousDate).format(Constants.FacilityTimeFormat)
                }
                else {
                    this.workflowDate = moment(this.shiftRecord.workflowTimeFacility).add(-1, 'day').format(Constants.FacilityTimeFormat);
                }

                this._shiftBlockService.loadList(this.shiftRecord.costCenterId, this.workflowDate).then(blocks => {
                    this.shiftBlocks = blocks.sort(sb => sb.startHour);
                    this.initializeForm();
                });
            });
        }

        this.formGroups = [ this.enterUOSForm ];
    }

    private async initializeForm() {
        if (!this.hasShiftBlocks()) {
            this.loadingRecords = false;
            this.enterUOSForm.updateValueAndValidity();
            return;
        }

        let existingRecords = await this.getExistingUnitOfServiceRecords();

        this.loadingRecords = false;

        let validators = [Validators.min(this.minValue), Validators.max(this.maxValue), Validators.pattern(Constants.NoDecimalRegex)];

        let shiftBlocksWithImports = this.shiftBlocksWithImports(existingRecords);

        // The control for the 'Reviewed' toggle that will be added if the staffing plans have imported values.
        let reviewedControl = new UntypedFormControl(false, Validators.requiredTrue);

        this.shiftBlocks.forEach(block => {
            let uosControl = new UntypedFormControl('', validators);

            this.enterUOSForm.addControl(this.getShiftBlockControlName(block.id), uosControl);

            let uosRecord = existingRecords.find(x => x.shiftBlockId == block.id && !x.isProjected);

            if (uosRecord) {
                uosControl.setValue(uosRecord.total);
                if(uosRecord.importedAmount){
                    if(!uosRecord.total){
                        uosRecord.total = uosRecord.importedAmount;
                        uosControl.setValue(uosRecord.importedAmount);
                    }
                    uosControl.setValidators(null);
                    uosControl.disable();
                }
            }
            else {
                uosRecord = new UnitOfServiceRecord();
                uosRecord.shiftBlockId = block.id;
                uosRecord.workflowTimeFacility = this.getFormattedDate(block);
                uosRecord.unitOfServiceId = this.ancillaryConfiguration.unitOfServiceId;
            }

            this.uosRecords.push(uosRecord);

            // Set a change subscription to the control rather than the form because the form level subscription
            // will ignore disabled controls when evaluating variances.
            uosControl.valueChanges
                      // There is a bug in angular that causes valueChanges subscriptions to fire twice for number inputs.
                      // distinctUntilChanged is an rxjs operator that can be used as a work around until SMT is upgraded
                      // to a version of angular where this bug is corrected.
                      .pipe(distinctUntilChanged())
                      .pipe(debounceTime(Constants.DebounceTime))
                      .subscribe(value => {
                            this.setUosTotal();

                            // If import values have been used and at least one of the values have changed then disable the review toggle.
                            if (shiftBlocksWithImports.some(n => n == block.id) && reviewedControl.enabled) {
                                let existingUosRecord = existingRecords.find(u => u.shiftBlockId == block.id);

                                if (existingUosRecord && existingUosRecord.total != value) {
                                    reviewedControl.setValue(false);
                                    reviewedControl.disable();
                                }
                            }
                        });
        });

        // If the import does need to be used then add the reviewed toggle.
        if (this.hasTotalUosImports()) {
            this.enterUOSForm.addControl('reviewed', reviewedControl);
        }

        // Initialize the total amount.
        this.setUosTotal();
    }

    private setUosTotal() {
        this.totalUos = 0;

        if (this.hasShiftBlocks()) {
            this.shiftBlocks.forEach(block => {
                let control = this.enterUOSForm.controls[this.getShiftBlockControlName(block.id)];
                let controlValue = +control.value;

                if (control && MiscUtil.isValidNonNegativeNumber(controlValue)) {
                    this.totalUos += controlValue;
                }
            });
        }
    }

    private getFormattedDate(shiftBlock: ShiftBlock) : string {
        var blockTime = moment(this.workflowDate).hour(shiftBlock.startHour);
        return blockTime.format(Constants.FacilityTimeFormat);
    }

    private async getExistingUnitOfServiceRecords() : Promise<UnitOfServiceRecord[]> {
        let workflowDates = [];
        if (this.ancillaryConfiguration.enterUOSByBlock) {
            workflowDates.push(this.workflowDate);
        } else {
            this.shiftBlocks.forEach(block => {
                workflowDates.push(this.getFormattedDate(block));
            });
        }
        return await this._uosRecordService.getUnitOfServiceRecordsForAncillaryPatient(this.shiftRecord.costCenterId, workflowDates) || [];
    }

    isFormValid(): boolean {
        return !this.loadingRecords && (!this.hasShiftBlocks() || (this.enterUOSForm && this.enterUOSForm.valid && this.formHasAnyValues()));
    }

    save(): Promise<any> {
        this.setStatus();
        this.setUosTotals();
        this._uosRecordService.saveUnitOfServiceRecords(this.uosRecords, this.shiftRecord.costCenterId);
        return this._shiftRecordService.setShiftRecordWithAssociated(this.shiftRecord);
    }

    private shiftBlocksWithImports(uosRecords: UnitOfServiceRecord[]): number[] {
        if (MiscUtil.checkArray(uosRecords)) {
            return uosRecords.filter(u => MiscUtil.isValidNumber(u.importedAmount)).map(u => u.shiftBlockId);
        }

        return [];
    }

    shouldSkipSave(): boolean {
        return false;
    }

    getShiftBlockControlName(shiftBlockId: number) : string {
        return 'block' + shiftBlockId;
    }

    getWorkflowDayString() {
        let time = moment(this.workflowDate);
        if (this.ancillaryConfiguration.enterUOSByBlock) {
            return this._utility.formatShiftTime(this.shiftRecord.prevShiftRecord.shiftBlock, this.workflowDate);
        } else {
            return time.format(WorkflowConstants.MomentFormat);
        }
    }

    getShiftBlockLabel(shiftBlock: ShiftBlock) {
        return this._utility.formatShiftTimeWithFormat(shiftBlock, this.workflowDate, WorkflowConstants.MomentFormat + ' (ha)', 'ha', ' - ha');
    }

    valueOutOfRange(shiftBlockId: number) : boolean {
        const safeShitBlockId = encodeURIComponent(this.getShiftBlockControlName(shiftBlockId));
        let control = this.enterUOSForm.get(safeShitBlockId.replace(/%20/g, ' '));

        return control && !control.valid && (control.touched || control.dirty) && (control.errors.min || control.errors.max || control.errors.pattern);
    }

    private editImported() {
        this.editingImportedRecords = !this.editingImportedRecords;

        let shiftBlocksWithImports = this.shiftBlocksWithImports(this.uosRecords);

        shiftBlocksWithImports.forEach(shiftBlockId => {
            let control = this.enterUOSForm.controls[this.getShiftBlockControlName(shiftBlockId)];

            if (control) {
                if (this.editingImportedRecords) {
                    control.enable();
                }
                else {
                    control.disable();
                }
            }
        });
    }

    hasShiftBlocks() {
        return this.shiftBlocks && this.shiftBlocks.length > 0;
    }

    enterByDay() {
        return !this.ancillaryConfiguration.enterUOSByBlock;
    }

    private formHasAnyValues() : boolean {
        let hasValues = false;
        if (this.shiftBlocks) {
            this.shiftBlocks.forEach(block => {
                const safeShitBlockName = encodeURIComponent(this.getShiftBlockControlName(block.id));
                let control = this.enterUOSForm.get(safeShitBlockName.replace(/%20/g, ' '));
                if (control && control.value != null && control.value != undefined && control.value !== "") {
                    hasValues = true;
                }
            });
        }

        return hasValues;
    }

    private setUosTotals() {
        this.uosRecords.forEach(record => {
            record.isProjected = false;
            let control = this.enterUOSForm.controls[this.getShiftBlockControlName(record.shiftBlockId)];

            if (control) {
                record.total = control.value;
            }
        });
    }

    private setStatus() {
        if (this.shiftRecord.status !== ShiftRecordStatus.Done) {
            this.shiftRecord.status = ShiftRecordStatus.ReviewPrevious;
        }
    }

    public openImportedValuesModal(shiftBlock: ShiftBlock) {
        var lastShiftBlock = this.shiftBlocks.reduce(function(prev, current) {
            return (prev.id > current.id) ? prev : current
           });
           var firstShiftBlock = this.shiftBlocks.reduce(function(prev, current) {
            return (prev.id < current.id) ? prev : current
           });
        this._modal.open(
            ImportTotalUosModalComponent,
            {
                width: '90%',
                height: '80%',
                data: {
                    title: 'Total UOS Imported Values',
                    shiftBlock: shiftBlock,
                    workflowDate: this.workflowDate,
                    costCenter: this.shiftRecord.costCenter,
                    isFirstShiftBlock: shiftBlock.id == firstShiftBlock.id,
                    isLastShiftBlock:  shiftBlock.id == lastShiftBlock.id
                }
            });
    }

    private hasTotalUosImportConfiguration(shiftBlock: ShiftBlock): boolean {
        return this.shiftRecord &&
            this.shiftRecord.costCenter &&
            (WorkflowPageUtil.hasTotalUosImports(this.client, this.shiftRecord.costCenter) || this.shiftBlockHasImportedData(shiftBlock));
    }

    private hasTotalUosImports(): boolean {
        return this.hasShiftBlocks() && this.shiftBlocks.some(s => this.hasTotalUosImportsByBlock(s));
    }

    private hasTotalUosImportsByBlock(shiftBlock: ShiftBlock): boolean {
        return this.shiftRecord && this.shiftBlockHasImportedData(shiftBlock);
    }

    private shiftBlockHasImportedData(shiftBlock: ShiftBlock): boolean {
        return MiscUtil.checkArray(this.uosRecords) && this.uosRecords.some(u => u.shiftBlockId == shiftBlock.id && MiscUtil.isValidNumber(u.importedAmount));
    }
}
